import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useListService from '@/data/hook/useListService';
import { courseLessonPlanQueries } from '@/data/query-keys/courseLessonPlanQueryKey';
import { lessonPlanQueries } from '@/data/query-keys/lessonPlanQueryKey';
import { createCourseLessonPlan } from '@/data/services/courseLessonPlanServices';
import {
  createLessonPlan,
  removeLessonPlan,
  updateLessonPlan,
} from '@/data/services/lessonPlanServices';
import LessonPlan from '@/models/LessonPlan';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';

type UseLessonPlansProps = {
  coursePathId: number;
};

type CreateMutationArgs = {
  name: string;
};

type UpdateMutationArgs = {
  lessonPlanId: number;
  data: Partial<LessonPlan>;
};

const useLessonPlans = ({ coursePathId }: UseLessonPlansProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const {
    results: courseLessonPlans,
    isInitialLoading: isLoadingCourseLessonPlans,
    cacheManager: courseLessonPlanCache,
  } = useListService({
    ...courseLessonPlanQueries.list({
      coursePathId: [coursePathId],
      pageSize: 1,
    }),
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
    enabled: !!coursePathId,
  });

  const courseLessonPlan = courseLessonPlans.at(0);

  const {
    results: lessonPlans,
    isInitialLoading,
    cacheManager,
  } = useListService({
    ...lessonPlanQueries.list({
      coursePathId: [coursePathId],
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    enabled: !!coursePathId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const handleCreate = async ({ name }: CreateMutationArgs) => {
    let courseLessonPlanId = courseLessonPlan?.id;
    if (!courseLessonPlan) {
      const createdCourseLessonPlan = await createCourseLessonPlan({
        coursePathId,
      });
      courseLessonPlanCache.add(createdCourseLessonPlan);
      courseLessonPlanId = createdCourseLessonPlan.id;
    }
    return createLessonPlan({ name, courseLessonPlanId: courseLessonPlanId });
  };

  const createMutation = useMutation({
    mutationFn: handleCreate,
    onSuccess: lessonPlan => {
      cacheManager.add(lessonPlan);
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ lessonPlanId, data }: UpdateMutationArgs) =>
      updateLessonPlan(lessonPlanId, data),
    onSuccess: lessonPlan => {
      cacheManager.update(lessonPlan);
      alert.success(t('updateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonPlanId: number) => removeLessonPlan(lessonPlanId),
    onSuccess: (_, lessonPlanId) => {
      cacheManager.remove(lessonPlanId);
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    lessonPlans,
    cacheManager,
    isLoading: isInitialLoading || isLoadingCourseLessonPlans,
    createLessonPlan: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateLessonPlan: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteLessonPlan: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useLessonPlans;
